// Connectors and linked folders (CER-21): what the database hands the apps,
// read field by field (a shape the apps do not expect is an error to show,
// never a listing quietly dropped), and the words both apps show, made here
// once. A folder a scan did not find is "not seen in the last scan", never
// "deleted": a computer that is off, a drive that is unplugged and a folder
// that was moved all look the same from here. A folder that could not be
// opened "couldn't be read", which is not empty. No path is ever shown: a
// folder is its root's label and the names below it.

import type { ContainerKind } from './types/index.ts';

/** Levels a scan lists under a root (connector_report_scan). A folder at the last level is listed but never opened, so it cannot be linked. */
export const SCAN_MAX_DEPTH = 3;

/** What a connector may do. Kept against private.connector_scopes() by connectors.test.ts. */
export const CONNECTOR_SCOPES = ['files:report', 'sources:process', 'export:read', 'health:read'] as const;
export type ConnectorScope = (typeof CONNECTOR_SCOPES)[number];

export const CONNECTOR_SCOPE_LABELS: Record<ConnectorScope, string> = {
  'files:report': 'Report folder listings',
  'sources:process': 'Process captured sources',
  'export:read': 'Read an export of my data',
  'health:read': 'Read system health (problems, job status, alert history; never your items)',
};

/** A row of connector_root_status: a root a connector offered, with its latest scans. */
export interface ConnectorRoot {
  connector_id: string;
  root_key: string;
  label: string;
  offered_at: string;
  approved_at: string | null;
  last_scan_at: string | null;
  last_scan_ok: boolean | null;
  last_scan_error: string | null;
  /** Where the latest scan read its file ids (wsl, windows...). */
  last_scan_runtime: string | null;
  last_ok_scan_at: string | null;
  last_ok_entry_count: number | null;
  last_ok_scan_runtime: string | null;
}

export const CONNECTOR_ROOT_COLUMNS =
  'connector_id, root_key, label, offered_at, approved_at, last_scan_at, last_scan_ok, last_scan_error, last_scan_runtime, last_ok_scan_at, last_ok_entry_count, last_ok_scan_runtime';

/** How a connector's key is held, in words. Reported by the device; shown, never trusted. */
export const KEY_STORAGE_LABELS: Record<string, string> = {
  windows_tpm: "this computer's TPM (Windows)",
};

export const keyStorageLabel = (storage: string | null): string =>
  storage === null ? 'no key' : KEY_STORAGE_LABELS[storage] ?? storage;

/**
 * A pairing waiting for its owner: what the computer claims about itself,
 * which the person approving reads back against the machine in front of them.
 * Nothing here belongs to an account yet; approving is what binds it.
 */
export interface PendingPairing {
  user_code: string;
  proposed_name: string;
  scopes: string[];
  /** Where it reads file ids: wsl, windows... */
  runtime: string;
  key_storage: string;
  /** Whether the computer asks its user to unlock the key at every use. */
  unlock_required: boolean;
  /** The key's fingerprint, which the computer shows too, so the two can be compared. */
  key_id: string;
  expires_at: string;
}

/** A row of connector_device_status: a connector with the key its computer holds. */
export interface ConnectorDevice {
  connector_id: string;
  name: string;
  scopes: string[];
  created_at: string;
  last_seen_at: string | null;
  revoked_at: string | null;
  /** Null when no computer is paired: the connector exists but nothing can act as it. */
  key_id: string | null;
  key_storage: string | null;
  unlock_required: boolean | null;
  runtime: string | null;
  paired_at: string | null;
  last_used_at: string | null;
}

export const CONNECTOR_DEVICE_COLUMNS =
  'connector_id, name, scopes, created_at, last_seen_at, revoked_at, key_id, key_storage, unlock_required, runtime, paired_at, last_used_at';

export interface FolderEntry {
  stable_id: string;
  name: string;
  kind: 'dir' | 'file';
  size: number | null;
  modified_at: string | null;
  /** A folder the connector could not open in the scan that last saw it. */
  unreadable: boolean;
  last_seen_scan_at: string;
  seen_in_last_scan: boolean;
}

/** One linked folder, as folder_binding_listing() gives it. */
export interface FolderBinding {
  id: string;
  connector_id: string;
  connector_name: string;
  connector_revoked: boolean;
  root_key: string;
  root_label: string;
  root_approved: boolean;
  /** Where the linked file id was read (wsl, windows...). */
  runtime: string;
  /** The folder names from the root down to the folder, as when it was linked. */
  relative_segments: string[];
  folder_stable_id: string;
  linked_name: string;
  linked_at: string;
  last_scan_at: string | null;
  last_scan_ok: boolean | null;
  last_scan_runtime: string | null;
  last_ok_scan_at: string | null;
  last_ok_scan_runtime: string | null;
  /** The latest good scan read ids in another runtime: this link's id means nothing there. */
  needs_repair: boolean;
  /** The folder's name now, when it is known. */
  folder_name: string | null;
  /** The linked id turned up with another name or in another folder: it may not be the same folder. */
  folder_changed: boolean;
  folder_last_seen_at: string | null;
  folder_seen_in_last_scan: boolean;
  folder_unreadable: boolean;
  entries: FolderEntry[];
}

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value);
const isText = (value: unknown): value is string => typeof value === 'string';
const isTextOrNull = (value: unknown): value is string | null => value === null || typeof value === 'string';
const isBoolOrNull = (value: unknown): value is boolean | null => value === null || typeof value === 'boolean';
const isSizeOrNull = (value: unknown): value is number | null => value === null || (typeof value === 'number' && Number.isInteger(value) && value >= 0);

/** connector_pairing_pending()'s answer; throws when it is not one. */
export function parsePendingPairing(data: unknown): PendingPairing {
  if (!isRecord(data) || !isText(data.user_code) || !isText(data.proposed_name)
    || !Array.isArray(data.scopes) || !data.scopes.every(isText)
    || !isText(data.runtime) || !isText(data.key_storage) || typeof data.unlock_required !== 'boolean'
    || !isText(data.key_id) || !/^[0-9a-f]{32}$/.test(data.key_id) || !isText(data.expires_at)) {
    throw new Error('that pairing did not arrive');
  }
  return {
    user_code: data.user_code,
    proposed_name: data.proposed_name,
    scopes: data.scopes,
    runtime: data.runtime,
    key_storage: data.key_storage,
    unlock_required: data.unlock_required,
    key_id: data.key_id,
    expires_at: data.expires_at,
  };
}

/**
 * What a connector's key says about it: which computer holds it, in what, and
 * when it last signed something. A connector with no key cannot act at all.
 */
export function deviceKeyLines(device: ConnectorDevice, ago: (iso: string) => string): string[] {
  if (device.revoked_at !== null) return [`Revoked ${ago(device.revoked_at)}; its key was revoked with it and cannot be used again.`];
  if (device.key_id === null) {
    return ['No computer is paired, so nothing can act as this connector. Pair one to give it a key.'];
  }
  const lines = [
    `Key ${device.key_id} in ${keyStorageLabel(device.key_storage)}, which cannot export it. Paired ${ago(device.paired_at!)}.`,
  ];
  if (device.unlock_required) lines.push('That computer asks its user to unlock the key at every use.');
  lines.push(device.last_used_at ? `Last signed a request ${ago(device.last_used_at)}` : 'It has not signed a request yet');
  return lines;
}

function parseEntry(value: unknown): FolderEntry {
  if (!isRecord(value) || !isText(value.stable_id) || !isText(value.name) || (value.kind !== 'dir' && value.kind !== 'file')
    || !isSizeOrNull(value.size) || !isTextOrNull(value.modified_at) || !isText(value.last_seen_scan_at)
    || typeof value.seen_in_last_scan !== 'boolean' || typeof value.unreadable !== 'boolean') {
    throw new Error('a linked folder did not arrive');
  }
  return {
    stable_id: value.stable_id,
    name: value.name,
    kind: value.kind,
    size: value.size,
    modified_at: value.modified_at,
    unreadable: value.unreadable,
    last_seen_scan_at: value.last_seen_scan_at,
    seen_in_last_scan: value.seen_in_last_scan,
  };
}

const BINDING_TEXT = ['id', 'connector_id', 'connector_name', 'root_key', 'root_label', 'runtime', 'folder_stable_id', 'linked_name', 'linked_at'] as const;
const BINDING_TEXT_OR_NULL = ['last_scan_at', 'last_scan_runtime', 'last_ok_scan_at', 'last_ok_scan_runtime', 'folder_name', 'folder_last_seen_at'] as const;
const BINDING_FLAGS = ['connector_revoked', 'root_approved', 'needs_repair', 'folder_changed', 'folder_seen_in_last_scan', 'folder_unreadable'] as const;

/** folder_binding_listing()'s answer; throws when it is not one. */
export function parseFolderBindings(data: unknown): FolderBinding[] {
  if (!Array.isArray(data)) throw new Error('the linked folders did not arrive');
  return data.map((b): FolderBinding => {
    if (!isRecord(b) || BINDING_TEXT.some((f) => !isText(b[f])) || BINDING_TEXT_OR_NULL.some((f) => !isTextOrNull(b[f]))
      || BINDING_FLAGS.some((f) => typeof b[f] !== 'boolean') || !isBoolOrNull(b.last_scan_ok)
      || !Array.isArray(b.relative_segments) || b.relative_segments.length === 0 || !b.relative_segments.every(isText)
      || !Array.isArray(b.entries)) {
      throw new Error('a linked folder did not arrive');
    }
    return {
      id: b.id as string,
      connector_id: b.connector_id as string,
      connector_name: b.connector_name as string,
      connector_revoked: b.connector_revoked as boolean,
      root_key: b.root_key as string,
      root_label: b.root_label as string,
      root_approved: b.root_approved as boolean,
      runtime: b.runtime as string,
      relative_segments: b.relative_segments as string[],
      folder_stable_id: b.folder_stable_id as string,
      linked_name: b.linked_name as string,
      linked_at: b.linked_at as string,
      last_scan_at: b.last_scan_at as string | null,
      last_scan_ok: b.last_scan_ok,
      last_scan_runtime: b.last_scan_runtime as string | null,
      last_ok_scan_at: b.last_ok_scan_at as string | null,
      last_ok_scan_runtime: b.last_ok_scan_runtime as string | null,
      needs_repair: b.needs_repair as boolean,
      folder_name: b.folder_name as string | null,
      folder_changed: b.folder_changed as boolean,
      folder_last_seen_at: b.folder_last_seen_at as string | null,
      folder_seen_in_last_scan: b.folder_seen_in_last_scan as boolean,
      folder_unreadable: b.folder_unreadable as boolean,
      entries: b.entries.map(parseEntry),
    };
  });
}

/** A linked folder as it is shown: its root's label and the names below it, never a path. */
export const folderDisplayPath = (binding: Pick<FolderBinding, 'root_label' | 'relative_segments'>) =>
  [binding.root_label, ...binding.relative_segments].join(' › ');

/** Where a linked folder is: on one computer, not in Cerebro Diem, and not on this phone. */
export const folderLocation = (connectorName: string) => `On ${connectorName}, local to that computer`;

/**
 * The lines under a linked folder, given how the app writes a time ("5
 * minutes ago"): whether it needs linking again, has changed since it was
 * linked, could not be read, or was in the last scan, and when it was last
 * seen. Never "deleted".
 */
export function folderStatusLines(binding: FolderBinding, ago: (iso: string) => string): string[] {
  const lines: string[] = [];
  if (binding.connector_revoked) lines.push(`${binding.connector_name} was revoked, so this listing is no longer updated.`);
  else if (!binding.root_approved) lines.push(`${binding.root_label} is no longer approved, so this listing is no longer updated.`);
  const lastSeen = binding.folder_last_seen_at ? `Last seen ${ago(binding.folder_last_seen_at)}` : null;
  if (binding.needs_repair) {
    lines.push(`Needs repair: the latest scan read file ids in ${binding.last_ok_scan_runtime}, and this link was made in ${binding.runtime}. Link the folder again.`);
    if (lastSeen) lines.push(lastSeen);
    return lines;
  }
  if (binding.folder_changed) {
    lines.push(`Changed since linked: it was "${binding.linked_name}"${binding.folder_name && binding.folder_name !== binding.linked_name ? ` and is now "${binding.folder_name}"` : ' and is now in another folder'}. It may be a different folder, so its contents are not shown until you confirm it.`);
    return lines;
  }
  if (binding.folder_seen_in_last_scan && binding.last_scan_at) {
    lines.push(binding.folder_unreadable ? `Couldn't be read in the last scan (${ago(binding.last_scan_at)})` : `Last seen ${ago(binding.last_scan_at)}`);
    return lines;
  }
  lines.push(binding.last_scan_at ? `Not seen in the last scan (${ago(binding.last_scan_at)})` : 'Not seen in the last scan');
  if (lastSeen) lines.push(lastSeen);
  return lines;
}

/** The note beside one entry of a listing, or null when it was in the last scan and could be read. */
export function entryStatus(entry: FolderEntry, ago: (iso: string) => string): string | null {
  if (!entry.seen_in_last_scan) return `not seen in the last scan; last seen ${ago(entry.last_seen_scan_at)}`;
  return entry.unreadable ? "couldn't be read in the last scan" : null;
}

/** What to say when a listing has no entries, or null when it has some. */
export function emptyListingText(binding: FolderBinding): string | null {
  if (binding.entries.length > 0 || binding.folder_changed) return null;
  if (binding.folder_unreadable) return 'Its contents could not be read, so nothing is listed.';
  return binding.folder_seen_in_last_scan ? 'The latest scan found nothing in it.' : 'No listing to show.';
}

/** "2.0 KB", "512 B"; empty for a folder. */
export function formatSize(bytes: number | null): string {
  if (bytes === null) return '';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit++;
  }
  return unit === 0 ? `${value} B` : `${value.toFixed(1)} ${units[unit]}`;
}

/** A root's latest scan, in words. */
export function rootScanLine(root: ConnectorRoot, ago: (iso: string) => string): string {
  if (!root.approved_at) return 'Waiting for your approval; nothing is read until you approve it.';
  if (!root.last_scan_at) return 'Approved; not scanned yet.';
  const where = root.last_scan_runtime ? ` (file ids from ${root.last_scan_runtime})` : '';
  if (root.last_scan_ok) return `Last scan ${ago(root.last_scan_at)}${where}: ${root.last_ok_entry_count ?? 0} entries`;
  const good = root.last_ok_scan_at ? `; last good scan ${ago(root.last_ok_scan_at)}` : '';
  return `Last scan ${ago(root.last_scan_at)} failed (${root.last_scan_error ?? 'unknown'})${good}`;
}

/** The kinds of entity a folder can be linked to: every kind of container. */
export type BindableKind = ContainerKind;
